import { ApprovalMode } from "./approvalModes";
import type {
  ClientToolDeps,
  ClientToolExecutionResult,
  ClientTool,
} from "./ClientTools";
import {
  buildRouteDescriptions,
  findNavigableRoute,
  isParameterized,
  NAVIGABLE_ROUTES,
} from "./navigableRoutes";
import { NavigateConfirmation } from "./NavigateConfirmation";

// ---------------------------------------------------------------------------
// Navigate tool input types
// ---------------------------------------------------------------------------

/**
 * Route-specific parameters for the navigate tool.
 *
 * Every property is optional because each route reads a different subset;
 * `navigableRoutes.ts` declares which params a route reads and which of them
 * it requires.
 *
 * Nothing checks this interface against the schema below or the route
 * registry, so a new param has to be added by hand in all three: the route's
 * `params` in `navigableRoutes.ts`, `navigateInputSchema` below, and here.
 */
export interface NavigateParams {
  datasetId?: string;
  tab?: string;
  collectionId?: string;
  actionName?: string;
  actionOwner?: string;
  invocationId?: string;
  deviceId?: string;
  fileId?: string;
  triggerId?: string;
  workspaceId?: string;
}

/**
 * Input to the navigate client tool.
 */
export interface NavigateInput {
  /** The route to navigate to (must be one of the registered route names). */
  route: string;
  /** Route-specific parameters — which params apply depends on the route. */
  params?: NavigateParams;
}

// ---------------------------------------------------------------------------
// JSON Schema
// ---------------------------------------------------------------------------

const navigateInputSchema: Record<string, unknown> = {
  type: "object",
  properties: {
    route: {
      type: "string",
      enum: NAVIGABLE_ROUTES.map((r) => r.name),
      description: "The route to navigate to",
    },
    params: {
      type: "object",
      description: "Route-specific parameters (see route descriptions above)",
      properties: {
        datasetId: {
          type: "string",
          description: "The dataset ID (e.g. 'ds_abc123')",
        },
        tab: {
          type: "string",
          description: "Which tab to open on the dataset page",
        },
        collectionId: {
          type: "string",
          description: "The collection ID",
        },
        actionName: {
          type: "string",
          description: "The action name",
        },
        actionOwner: {
          type: "string",
          description:
            "The action owner (org or user). Omit for actions owned by the current org.",
        },
        invocationId: {
          type: "string",
          description: "The invocation ID",
        },
        deviceId: {
          type: "string",
          description: "The device ID",
        },
        fileId: {
          type: "string",
          description: "The file ID",
        },
        triggerId: {
          type: "string",
          description: "The trigger ID or name",
        },
        workspaceId: {
          type: "string",
          description: "The visualizer workspace ID (e.g. 'ws_abc123')",
        },
      },
      additionalProperties: false,
    },
  },
  required: ["route"],
  additionalProperties: false,
};

// ---------------------------------------------------------------------------
// Tool definition
// ---------------------------------------------------------------------------

function execute(
  params: Record<string, unknown>,
  deps?: ClientToolDeps,
): ClientToolExecutionResult {
  if (!deps?.goto) {
    return {
      response: { error: "Navigation is not available" },
      success: false,
    };
  }

  const input = params as unknown as NavigateInput;

  if (!input.route) {
    return {
      response: { error: "Missing required parameter: route" },
      success: false,
    };
  }

  const route = findNavigableRoute(input.route);
  if (!route) {
    return {
      response: { error: `Unknown route: ${input.route}` },
      success: false,
    };
  }

  try {
    let navigatedTo: string;

    if (isParameterized(route)) {
      const routeParams = (input.params ?? {}) as Record<
        string,
        string | undefined
      >;
      navigatedTo = route.execute(deps.goto, routeParams);
    } else {
      deps.goto.navigateTo(route.path);
      navigatedTo = route.path;
    }

    return {
      response: {
        navigated_to: navigatedTo,
        success: true,
      },
      success: true,
    };
  } catch (err) {
    // A parameterized route throws when a required param is missing. Caught
    // here because a throw that reached `ClientToolBatchRunner` would be
    // classified as a transport failure and read as retryable, when the model
    // has to change its arguments to get anywhere.
    return {
      response: {
        error: err instanceof Error ? err.message : String(err),
      },
      success: false,
    };
  }
}

const routeDescriptions = buildRouteDescriptions();

export const tool: ClientTool = {
  spec: {
    name: "client_navigate",
    description: `Navigate the user's browser to a page in the Roboto web app. Use this when the user asks to go to a page, view a resource, or when it would be helpful to direct them to a relevant page after answering a question.

Available routes:
${routeDescriptions}

Pass the route name as "route" and any required/optional parameters in "params".`,
    input_schema: navigateInputSchema,
  },
  execute: (params, deps) => Promise.resolve(execute(params, deps)),
  defaultApprovalMode: ApprovalMode.FOLLOW_AUTO,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Navigate",
  displayDescription: "Opens pages and visualizations in Roboto.",
  ConfirmationContent: NavigateConfirmation,
};
